import React, { useState } from 'react';
import {
  ServerIcon,
  FolderIcon,
  TableCellsIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const SelectionSummaryCards = ({ 
  selectedDatabases = [], 
  selectedSchemas = [], 
  selectedTables = [], 
  onModifySelection 
}) => {
  const [showAllTables, setShowAllTables] = useState(false);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  
  // Group tables by database and schema for better organization
  const organizeTablesByHierarchy = () => {
    const hierarchy = {};
    
    selectedTables.forEach(tableFullName => {
      const parts = tableFullName.split('.');
      if (parts.length >= 3) {
        const [database, schema, table] = parts;
        
        if (!hierarchy[database]) {
          hierarchy[database] = {};
        }
        if (!hierarchy[database][schema]) {
          hierarchy[database][schema] = [];
        }
        hierarchy[database][schema].push({
          name: table,
          fullName: tableFullName
        });
      }
    });
    
    return hierarchy;
  };

  // Filter tables based on search term
  const filteredTables = selectedTables.filter(table => 
    table.toLowerCase().includes(tableSearchTerm.toLowerCase())
  );

  const tableHierarchy = organizeTablesByHierarchy();

  // Create organized data that combines explicit selections with derived data from tables
  const organizedData = {
    // Extract unique database names from both explicit selections and table names
    databaseNames: [...new Set([
      ...selectedDatabases,
      ...selectedTables.map(table => table.split('.')[0]).filter(Boolean)
    ])],
    // Extract unique schema names from both explicit selections and table names
    schemaNames: [...new Set([
      ...selectedSchemas,
      ...selectedTables.map(table => {
        const parts = table.split('.');
        return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : null;
      }).filter(Boolean)
    ])],
    tableNames: selectedTables
  };

  const hasSelections = organizedData.databaseNames.length > 0 || organizedData.schemaNames.length > 0 || organizedData.tableNames.length > 0;

  if (!hasSelections) {
    return (
      <div className="text-center p-8 bg-gray-50 border border-gray-200 rounded-lg">
        <TableCellsIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Tables Selected</h3>
        <p className="text-sm text-gray-500 mb-4">
          Choose specific databases, schemas, and tables to focus your data lineage and validation features.
        </p>
        <button
          onClick={onModifySelection}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <PencilIcon className="h-4 w-4 mr-2" />
          Select Tables
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Databases Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <ServerIcon className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <div className="text-lg font-semibold text-blue-900">
                {organizedData.databaseNames.length}
              </div>
              <div className="text-sm text-blue-700">
                Database{organizedData.databaseNames.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          {organizedData.databaseNames.length > 0 && (
            <div className="mt-2 text-xs text-blue-600">
              {organizedData.databaseNames.slice(0, 2).join(', ')}
              {organizedData.databaseNames.length > 2 && ` +${organizedData.databaseNames.length - 2} more`}
            </div>
          )}
        </div>

        {/* Schemas Card */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <FolderIcon className="h-5 w-5 text-yellow-600 mr-2" />
            <div>
              <div className="text-lg font-semibold text-yellow-900">
                {organizedData.schemaNames.length}
              </div>
              <div className="text-sm text-yellow-700">
                Schema{organizedData.schemaNames.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          {organizedData.schemaNames.length > 0 && (
            <div className="mt-2 text-xs text-yellow-600">
              {organizedData.schemaNames.slice(0, 2).join(', ')}
              {organizedData.schemaNames.length > 2 && ` +${organizedData.schemaNames.length - 2} more`}
            </div>
          )}
        </div>

        {/* Tables Card */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <TableCellsIcon className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <div className="text-lg font-semibold text-green-900">
                {organizedData.tableNames.length}
              </div>
              <div className="text-sm text-green-700">
                Table{organizedData.tableNames.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          {organizedData.tableNames.length > 0 && (
            <div className="mt-2 text-xs text-green-600">
              {organizedData.databaseNames.length} database{organizedData.databaseNames.length !== 1 ? 's' : ''} affected
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          {organizedData.tableNames.length > 0 && (
            <button
              onClick={() => setShowAllTables(!showAllTables)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {showAllTables ? (
                <>
                  <ChevronDownIcon className="h-4 w-4 mr-1" />
                  Hide Tables
                </>
              ) : (
                <>
                  <ChevronRightIcon className="h-4 w-4 mr-1" />
                  View All Tables
                </>
              )}
            </button>
          )}
        </div>
        
        <button
          onClick={onModifySelection}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <PencilIcon className="h-4 w-4 mr-2" />
          Modify Selection
        </button>
      </div>

      {/* Expandable Tables View */}
      {showAllTables && organizedData.tableNames.length > 0 && (
        <div className="border border-gray-200 rounded-lg bg-white">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-900">Selected Tables</h4>
              <button
                onClick={() => setShowAllTables(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search tables..."
                value={tableSearchTerm}
                onChange={(e) => setTableSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto p-4">
            {tableSearchTerm ? (
              // Flat filtered list when searching
              <div className="space-y-1">
                {filteredTables.map(table => (
                  <div key={table} className="text-sm text-gray-700 font-mono bg-gray-50 px-3 py-2 rounded">
                    {table}
                  </div>
                ))}
                {filteredTables.length === 0 && (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    No tables match your search
                  </div>
                )}
              </div>
            ) : (
              // Hierarchical organized view when not searching
              <div className="space-y-4">
                {Object.entries(tableHierarchy).map(([database, schemas]) => (
                  <div key={database} className="border-l-2 border-blue-200 pl-3">
                    <div className="flex items-center mb-2">
                      <ServerIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="font-medium text-blue-900">{database}</span>
                    </div>
                    
                    <div className="ml-6 space-y-2">
                      {Object.entries(schemas).map(([schema, tables]) => (
                        <div key={`${database}.${schema}`} className="border-l-2 border-yellow-200 pl-3">
                          <div className="flex items-center mb-1">
                            <FolderIcon className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="font-medium text-yellow-800">{schema}</span>
                            <span className="ml-2 text-xs text-gray-500">({tables.length} tables)</span>
                          </div>
                          
                          <div className="ml-6 grid grid-cols-1 lg:grid-cols-2 gap-1">
                            {tables.map(table => (
                              <div key={table.fullName} className="flex items-center text-sm text-gray-600">
                                <TableCellsIcon className="h-3 w-3 text-gray-400 mr-2" />
                                {table.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {!tableSearchTerm && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              <InformationCircleIcon className="h-4 w-4 inline mr-1" />
              Tables are organized by database and schema. Use search to find specific tables quickly.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectionSummaryCards;